import { LinkIcon } from "@heroicons/react/24/outline";
import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { TfHullEvaluationVessel } from "../models/tf-hull-evaluations.models";

interface Column {
  label: string;
  value: (vessel: TfHullEvaluationVessel) => string | number | boolean | null;
}

const COLUMNS: Column[] = [
  { label: "IMO", value: (vessel) => vessel.imo },
  { label: "Vessel name", value: (vessel) => vessel.vesselName },
  { label: "Design", value: (vessel) => vessel.design },
  { label: "Has email in IMOS", value: (vessel) => vessel.hasEmailInImos },
  { label: "Tech form status", value: (vessel) => vessel.techFormStatus },
  { label: "Date", value: (vessel) => vessel.techFormDate },
  { label: "ZN link", value: (vessel) => vessel.znLink },
  { label: "Hull assessment check", value: (vessel) => vessel.hullAssessmentCheck },
  { label: "Submit hull assessment", value: (vessel) => vessel.submitHullAssessmentLink },
  { label: "Latest manual reminder", value: (vessel) => vessel.latestManualReminder },
  { label: "OPS link", value: (vessel) => vessel.opsLink },
  { label: "Delivered since", value: (vessel) => vessel.deliveredSinceDays },
  { label: "Entered since", value: (vessel) => vessel.enteredSinceDays },
  { label: "Delivery date", value: (vessel) => vessel.deliveryDate },
  { label: "Status", value: (vessel) => vessel.status },
  { label: "Contract type", value: (vessel) => vessel.contractType },
  { label: "Contract", value: (vessel) => vessel.contract },
  { label: "Min redel date", value: (vessel) => vessel.minRedeliveryDate },
  { label: "Max redel date", value: (vessel) => vessel.maxRedeliveryDate },
  { label: "Entry date", value: (vessel) => vessel.entryDate },
  { label: "Vessel type", value: (vessel) => vessel.vesselType },
  { label: "Comment", value: (vessel) => vessel.comment },
];

const renderCell = (value: ReturnType<Column["value"]>): ReactNode => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === "") return "—";
  if (typeof value === "string" && /^https?:\/\//.test(value)) {
    return (
      <a href={value} target="_blank" rel="noreferrer" aria-label="Open linked record">
        <LinkIcon className="h-4 w-4 text-green" />
      </a>
    );
  }
  return value;
};

interface Props {
  loading: boolean;
  vessels: TfHullEvaluationVessel[];
}

const TfHullEvaluationsTable = ({ loading, vessels }: Props) => (
  <div className="max-h-[800px] overflow-auto">
    <table className="w-full min-w-[2300px] text-[11px]" data-test="tf-hull-evaluations-table">
      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
        <tr>
          {COLUMNS.map((column) => <th key={column.label} className="border-b px-3 py-2 text-left">{column.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {vessels.map((vessel) => (
          <tr key={`${vessel.imo}-${vessel.contract ?? "no-contract"}`} className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-800/70">
            {COLUMNS.map((column) => (
              <td
                key={column.label}
                className={twMerge(
                  "whitespace-nowrap border-b px-3 py-2 dark:border-gray-700",
                  column.label === "Tech form status" && vessel.techFormStatus === "COMPLETED" && "font-semibold text-green",
                  column.label === "Tech form status" && vessel.techFormStatus === "NOT REQUESTED" && "font-semibold text-red-600",
                )}
              >
                {renderCell(column.value(vessel))}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {!loading && !vessels.length && <p className="p-6 text-center text-sm text-gray-500">No contracts match the current filters.</p>}
  </div>
);

export default TfHullEvaluationsTable;
